import { useState } from 'react';

import { cn } from '@/lib/utils';

export type AvatarProps = {
  name?: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

function initials(name?: string) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

/**
 * Avatar — used in lists, chat headers, profile cards. Fallback to initials
 * on tertiary-fixed colour wash if no image is provided.
 */
export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  const sizeClass = size === 'sm' ? 'h-8 w-8 text-xs' : size === 'lg' ? 'h-14 w-14 text-lg' : 'h-10 w-10 text-sm';

  /**
   * ⚠️ ЕСЛИ ФОТО НЕ ЗАГРУЗИЛОСЬ — ПОКАЗЫВАЕМ БУКВЫ, А НЕ СЛОМАННЫЙ ЗНАЧОК.
   *
   * 07.09.2026 нашёлся ученик, у которого в фото стоял адрес сервера, снесённого
   * при переезде в Казахстан: `http://174.138.11.247:8443/...`. Ссылку из базы
   * убрали, но такое повторится — люди меняют фото, файлы теряются, домены
   * переезжают. Буквы вместо битой картинки выглядят как задумано, а не как
   * поломка.
   */
  const [broken, setBroken] = useState(false);

  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name ?? 'avatar'}
        onError={() => setBroken(true)}
        className={cn('rounded-full object-cover', sizeClass, className)}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex items-center justify-center rounded-full bg-tertiary-fixed font-display font-semibold text-on-tertiary-fixed-variant',
        sizeClass,
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}
